import createState from '../State/createState';
import createPlayer from '../Player/createPlayer';
import ConsumerInput from './ConsumerInput';
import ConsumerOutput from './ConsumerOutput';
import ApiServices from '../Api/ApiServices';
import ErrorsHandler from '../Errors/ErrorsHandler';
import connectStoreSubscriptions from '../State/connectStoreSubscriptions';
import connectApiSubscriptions from '../Api/connectApiSubscriptions';
import connectPlayerSubscriptions from '../Player/connectPlayerSubscriptions';
import connectErrorSubscriptions from '../Errors/connectErrorSubscriptions';

/**
 * A class acting as a "mediator" between all domains of the application,
 * and the primary entry point into it.
 *
 * Each instance holds a self-contained instance of the middleware state
 * and manages the subscribing and unsubscribing of streams connecting
 * the various domains.
 */

export default class Middleware {
    constructor(parentElement, options) {
        this.state = createState({ config: options });
        this.errorsHandler = new ErrorsHandler();

        this.apiServices = new ApiServices(this);
        this.player = createPlayer(parentElement, this.state.stores.config.value);
        this.consumerInput = new ConsumerInput();
        this.consumerOutput = new ConsumerOutput(this.player, this.errorsHandler.consumerError$);

        this.subscriptions = [
            ...connectPlayerSubscriptions(this),
            ...connectApiSubscriptions(this),
            ...connectStoreSubscriptions(this),
            ...connectErrorSubscriptions(this),
        ];
    }

    get legacyShim$() {
        return this.consumerOutput.legacyShim$;
    }

    get playerInteractions$() {
        return this.consumerOutput.playerInteractions$;
    }

    /**
     * Configures the middleware with zero or more configuration
     * options.
     */

    configure(options) {
        this.consumerInput.config$.next(options);
    }

    /**
     * Provides a new asset to the middleware.
     */

    loadAsset(asset) {
        this.player.showControls();
        this.consumerInput.asset$.next(asset);
    }

    /**
     * Fades out the player volume.
     */

    fadeOutVolume() {
        this.player.fadeOutVolume();
    }

    /**
     * Fades in the player volume.
     */

    fadeInVolume() {
        this.player.fadeInVolume();
    }

    /**
     * Shows the controls if hidden/idle.
     */

    showControls() {
        this.player.showControls();
    }

    /**
     * Hides the controls if active.
     */

    hideControls() {
        this.player.hideControls();
    }

    /**
     * Exits fullscreen mode if enabled.
     */

    exitFullscreen() {
        this.player.exitFullscreen();
    }

    /**
     * Destroys the middleware by unsubscribing from all streams.
     */

    destroy() {
        this.disconnectSubscriptions();
        this.player.destroy();
    }

    /**
     * Disconnects all active stream subscriptions, and garbage collects
     * references to them.
     */

    disconnectSubscriptions() {
        while (this.subscriptions.length > 0) this.subscriptions.pop().unsubscribe();
    }
}
